import { ChangeEvent, useCallback } from 'react';
import {
  Stack,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Text,
  Box,
  Divider,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import ButtonTab from '@Components/Common/ButtonTab';
import NotifyAdmin from '@Components/FilterNotification/NotifyAdmin';
import NotifyUser from '@Components/FilterNotification/NotifyUser';
import { useWhyDidYouUpdate } from '@Utils/hooks';
import {
  EnableDisableStatus,
  ExchangeOnlinePolicyItem,
} from '@Policy/utils/types';
import ApprovedBlockedSenders from '../components/ApprovedBlockedSenders';

function NotificationApprovedListTab() {
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;

  const ntfEnableNotifyRecipient = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfEnableNotifyRecipient
  );
  const ntfInternalRecipientSubject = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfInternalRecipientSubject
  );
  const ntfInternalRecipientMessage = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfInternalRecipientMessage
  );
  const ntfEnableNotifyAdmin = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfEnableNotifyAdmin
  );
  const ntfToAccount = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfToAccount
  );
  const ntfAdminSubject = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminSubject
  );
  const ntfAdminMessage = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminMessage
  );
  const ntfAdminNotifySettingType = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminNotifySettingType
  );
  const ntfAdminIntervalNum = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminIntervalNum
  );
  const ntfAdminIntervalType = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminIntervalType
  );
  const ntfAdminOccurenceNum = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfAdminOccurenceNum
  );
  const ntfRecipientGroupId = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfRecipientGroupId
  );
  const ntfRecipientGroupEnabled = policySelector(
    (s) => s.tmRiskAiFilters.notification.ntfRecipientGroupEnabled
  );
  const recipientGroups = contextSelector((s) => s.recipientGroups);

  const setRiskAiNtfState = contextSelector((s) => s.setRiskAiNtfState);

  const handleNtfEnableNotifyRecipientChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setRiskAiNtfState({
        ntfEnableNotifyRecipient: e.target.checked ? 1 : 0,
      });
    },
    [setRiskAiNtfState]
  );
  const handleNtfInternalRecipientSubjectChange = useCallback(
    (subject: string) =>
      setRiskAiNtfState({ ntfInternalRecipientSubject: subject }),
    [setRiskAiNtfState]
  );
  const handleNtfInternalRecipientMessageChange = useCallback(
    (message: string) =>
      setRiskAiNtfState({ ntfInternalRecipientMessage: message }),
    [setRiskAiNtfState]
  );
  const handleNtfEnableNotifyAdminChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setRiskAiNtfState({ ntfEnableNotifyAdmin: e.target.checked ? 1 : 0 });
    },
    [setRiskAiNtfState]
  );
  const handleNtfToAccountChange = useCallback(
    (toAccount: string) => setRiskAiNtfState({ ntfToAccount: toAccount }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminSubjectChange = useCallback(
    (subject: string) => setRiskAiNtfState({ ntfAdminSubject: subject }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminMessageChange = useCallback(
    (message: string) => setRiskAiNtfState({ ntfAdminMessage: message }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminNotifySettingTypeChange = useCallback(
    (notifySettingType: number) =>
      setRiskAiNtfState({ ntfAdminNotifySettingType: notifySettingType }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminIntervalNumChange = useCallback(
    (intervalNum: number) =>
      setRiskAiNtfState({ ntfAdminIntervalNum: intervalNum }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminIntervalTypeChange = useCallback(
    (intervalType: number) =>
      setRiskAiNtfState({ ntfAdminIntervalType: intervalType }),
    [setRiskAiNtfState]
  );
  const handleNtfAdminOccurenceNumChange = useCallback(
    (occurenceNum: number) =>
      setRiskAiNtfState({ ntfAdminOccurenceNum: occurenceNum }),
    [setRiskAiNtfState]
  );
  const handleNtfRecipientGroupIdChange = useCallback(
    (recipientGroupId: number) => {
      setRiskAiNtfState({ ntfRecipientGroupId: recipientGroupId });
    },
    [setRiskAiNtfState]
  );
  const handleNtfRecipientGroupEnabledChange = useCallback(
    (enabled: EnableDisableStatus) => {
      setRiskAiNtfState({ ntfRecipientGroupEnabled: enabled });
    },
    [setRiskAiNtfState]
  );

  useWhyDidYouUpdate('NotificationApprovedListTab', {
    ntfEnableNotifyRecipient,
    ntfInternalRecipientSubject,
    ntfInternalRecipientMessage,
    ntfEnableNotifyAdmin,
    ntfToAccount,
    ntfAdminSubject,
    ntfAdminMessage,
    ntfAdminNotifySettingType,
    ntfAdminIntervalNum,
    ntfAdminIntervalType,
    ntfAdminOccurenceNum,
    ntfRecipientGroupId,
    ntfRecipientGroupEnabled,
    recipientGroups,
  });

  return (
    <Stack gap="6x" p="4x">
      <Text size="lg">{t('policy.notification')}</Text>
      <Tabs>
        <TabList>
          <ButtonTab>{t('policy.notification.admin')}</ButtonTab>
          <ButtonTab>{t('policy.notification.user')}</ButtonTab>
        </TabList>
        <TabPanels py="24px">
          <TabPanel>
            <NotifyAdmin
              status={ntfEnableNotifyAdmin}
              toAccount={ntfToAccount}
              notifySettingType={ntfAdminNotifySettingType}
              subject={ntfAdminSubject}
              message={ntfAdminMessage}
              intervalNum={ntfAdminIntervalNum}
              intervalType={ntfAdminIntervalType}
              occurenceNum={ntfAdminOccurenceNum}
              recipientGroupId={ntfRecipientGroupId}
              recipientGroups={recipientGroups}
              recipientGroupEnabled={ntfRecipientGroupEnabled}
              setStatus={handleNtfEnableNotifyAdminChange}
              setToAccount={handleNtfToAccountChange}
              setNotifySettingType={handleNtfAdminNotifySettingTypeChange}
              setSubject={handleNtfAdminSubjectChange}
              setMessage={handleNtfAdminMessageChange}
              setIntervalNum={handleNtfAdminIntervalNumChange}
              setIntervalType={handleNtfAdminIntervalTypeChange}
              setOccurenceNum={handleNtfAdminOccurenceNumChange}
              setRecipientGroupId={handleNtfRecipientGroupIdChange}
              setRecipientGroupEnabled={handleNtfRecipientGroupEnabledChange}
            />
          </TabPanel>
          <TabPanel>
            <NotifyUser
              status={ntfEnableNotifyRecipient}
              subject={ntfInternalRecipientSubject}
              message={ntfInternalRecipientMessage}
              setStatus={handleNtfEnableNotifyRecipientChange}
              setSubject={handleNtfInternalRecipientSubjectChange}
              setMessage={handleNtfInternalRecipientMessageChange}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Divider />
      <Text size="lg">{t('policy.tab.approvedList')}</Text>
      <Box ml="4x">
        <ApprovedBlockedSenders />
      </Box>
    </Stack>
  );
}

export default NotificationApprovedListTab;
